import React, { useMemo } from 'react';
import { ActiveVisitor } from '../hooks/useAnalyticsWebSocket';
import { MapPinOff, ShieldAlert, WifiOff, Globe, Info, Activity } from 'lucide-react';

interface GeolocationInsightsProps {
    visitors: ActiveVisitor[];
}

const REASON_LABELS: Record<string, { label: string; icon: any; color: string; bgColor: string }> = {
    local_ip: {
        label: 'Local/Private IP',
        icon: ShieldAlert,
        color: 'text-amber-600',
        bgColor: 'bg-amber-50'
    },
    no_ip: {
        label: 'No IP Detected',
        icon: WifiOff,
        color: 'text-neutral-500',
        bgColor: 'bg-neutral-50'
    },
    bogon_ip: {
        label: 'Bogon/Reserved',
        icon: Globe,
        color: 'text-purple-600',
        bgColor: 'bg-purple-50'
    },
    unsupported_ip: {
        label: 'Unsupported IP',
        icon: Info,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50'
    },
    api_error: {
        label: 'API Timeout/Error',
        icon: Activity,
        color: 'text-red-600',
        bgColor: 'bg-red-50'
    },
    unknown: {
        label: 'Unknown Reason',
        icon: MapPinOff,
        color: 'text-neutral-400',
        bgColor: 'bg-neutral-100'
    }
};

export const GeolocationInsights: React.FC<GeolocationInsightsProps> = ({ visitors }) => {
    const insights = useMemo(() => {
        const total = visitors.length;
        const unableToLocate = visitors.filter(v => !v.location?.located).length;
        const located = total - unableToLocate;

        const reasons: Record<string, number> = {};
        visitors.forEach(v => {
            if (!v.location?.located) {
                const reason = v.location?.reason || 'unknown';
                reasons[reason] = (reasons[reason] || 0) + 1;
            }
        });

        return {
            total,
            located,
            unableToLocate,
            reasons: Object.entries(reasons)
                .map(([key, count]) => ({
                    key,
                    count,
                    ...(REASON_LABELS[key] || REASON_LABELS.unknown)
                }))
                .sort((a, b) => b.count - a.count)
        };
    }, [visitors]);

    return (
        <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col h-[320px] shadow-sm overflow-hidden">
            <div className="flex items-center justify-between mb-6">
                <div className="flex flex-col">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-900">Geolocation Health</h3>
                    <span className="text-[10px] text-neutral-400 font-medium tracking-tight">Geographic intelligence coverage</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex flex-col items-end">
                        <span className="text-lg font-black text-neutral-900 tabular-nums leading-none">
                            {insights.unableToLocate}
                        </span>
                        <span className="text-[9px] font-bold text-neutral-400 uppercase tracking-tighter mt-0.5">Hidden</span>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto pr-1 custom-scrollbar">
                {/* Progress Bar */}
                <div className="mb-6 space-y-2">
                    <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest">
                        <span className="text-blue-600">Located ({insights.located})</span>
                        <span className="text-neutral-400">Total ({insights.total})</span>
                    </div>
                    <div className="w-full h-3 bg-neutral-50 rounded-full overflow-hidden flex border border-neutral-100/50">
                        <div
                            className="h-full bg-blue-500 transition-all duration-1000 shadow-[inset_-2px_0_4px_rgba(0,0,0,0.05)]"
                            style={{ width: `${(insights.located / (insights.total || 1)) * 100}%` }}
                        />
                    </div>
                </div>

                {/* Reasons List */}
                <div className="space-y-2.5">
                    {insights.reasons.length > 0 ? (
                        insights.reasons.map((reason) => (
                            <div
                                key={reason.key}
                                className="flex items-center justify-between p-2.5 rounded-xl border border-neutral-50 bg-white hover:border-neutral-200 transition-all group"
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-lg ${reason.bgColor} ${reason.color} group-hover:scale-110 transition-transform`}>
                                        <reason.icon size={14} />
                                    </div>
                                    <span className="text-[11px] font-bold text-neutral-600 group-hover:text-neutral-900 transition-colors uppercase tracking-tight">
                                        {reason.label}
                                    </span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs font-black text-neutral-900 tabular-nums">{reason.count}</span>
                                    <span className="text-[10px] font-bold text-neutral-300 w-8 text-right">
                                        {((reason.count / (insights.total || 1)) * 100).toFixed(0)}%
                                    </span>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="flex flex-col items-center justify-center py-8 text-neutral-300">
                            <div className="w-12 h-12 rounded-full flex items-center justify-center bg-emerald-50 text-emerald-500 mb-3 shadow-[0_0_20px_rgba(16,185,129,0.1)] border border-emerald-100">
                                <Activity size={20} />
                            </div>
                            <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Perfect Coverage</span>
                            <span className="text-[9px] font-medium text-neutral-400 mt-1">All visitors successfully located</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
